import apiClient, { formEncodedApiClient } from './axios.js';
import csrfManager from './utils/csrfMainManager.js';
import tokenBlacklistService from '../tokenUtils/tokenBlacklistService.js';

// Production-safe error normalization
const normalizeError = (error, fallback = 'Request failed') => {
  if (typeof error === 'string') return error;
  if (error?.response?.data?.detail) return String(error.response.data.detail);
  if (error?.response?.data?.message) return String(error.response.data.message);
  if (error?.message) return String(error.message);
  return fallback;
};

// Rate limiting helper
const createRateLimiter = (maxCalls, windowMs) => {
  const calls = [];
  return () => {
    const now = Date.now();
    // Remove old calls outside the window
    while (calls.length > 0 && calls[0] <= now - windowMs) {
      calls.shift();
    }
    // Check if we're at the limit
    if (calls.length >= maxCalls) {
      const oldestCall = calls[0];
      const timeUntilReset = windowMs - (now - oldestCall);
      throw new Error(`Too many requests. Try again in ${Math.ceil(timeUntilReset / 1000)} seconds.`);
    }
    calls.push(now);
  };
};

class AuthService {
  constructor() {
    this.isInitialized = false;
    this.initializationPromise = null;
    this._operationStates = {
      login: { inProgress: false, lastAttempt: 0, startedAt: 0 },
      register: { inProgress: false, lastAttempt: 0, startedAt: 0 },
      logout: { inProgress: false, lastAttempt: 0, startedAt: 0 },
      refresh: { inProgress: false, lastAttempt: 0, startedAt: 0 }
    };
    
    // Rate limiters for different operations
    this.rateLimiters = {
      login: createRateLimiter(5, 60000), // 5 attempts per minute
      register: createRateLimiter(3, 300000), // 3 attempts per 5 minutes
      refresh: createRateLimiter(10, 60000) // 10 attempts per minute
    };
  }

  async initialize() {
    if (this.isInitialized) return;
    if (this.initializationPromise) return this.initializationPromise;

    this.initializationPromise = this._performInitialization();
    return this.initializationPromise;
  }

  async _performInitialization() {
    try {
      await csrfManager.initialize();
      
      // Clean up any expired tokens
      if (tokenBlacklistService?.cleanupBlacklist) {
        try {
          tokenBlacklistService.cleanupBlacklist();
        } catch (cleanupError) {
          console.warn('[AuthService] Token cleanup failed:', cleanupError.message);
        }
      }
      
      this.isInitialized = true;
      //console.log('[AuthService] Initialized successfully');
    } catch (error) {
      console.warn('[AuthService] Initialization failed:', error.message);
      // Allow degraded mode operation
      this.isInitialized = true;
    }
  }

  _checkOperationState(operation) {
    const state = this._operationStates[operation];
    if (state.inProgress) {
      // Every call site sets inProgress=true right before an awaited network
      // call with its own timeout (10s for login), inside a try/finally that
      // always resets it back to false. In normal operation this flag can
      // never outlive that request. But if it ever does (a stuck HMR module
      // instance, a request whose promise never settles for some unforeseen
      // reason), this lock would otherwise block every future attempt in
      // this tab forever, with no way for the user to recover except a full
      // page reload -- and the resulting error gets mapped to the generic
      // "Email or password is incorrect" message in the UI, making it look
      // like a credentials problem instead of a stuck client-side lock.
      // Self-heal: treat a lock older than 15s (well past any request's own
      // timeout) as stale and clear it rather than blocking indefinitely.
      const lockAge = Date.now() - state.startedAt;
      if (lockAge > 15000) {
        state.inProgress = false;
      } else {
        throw new Error(`${operation} operation already in progress. Please wait.`);
      }
    }

    // Check for rapid successive calls
    const timeSinceLastAttempt = Date.now() - state.lastAttempt;
    if (timeSinceLastAttempt < 1000) {
      throw new Error(`Please wait before attempting ${operation} again.`);
    }
  }

  _setOperationState(operation, inProgress) {
    const state = this._operationStates[operation];
    state.inProgress = inProgress;
    if (inProgress) {
      state.startedAt = Date.now();
    } else {
      state.lastAttempt = Date.now();
    }
  }

  async login(username, password) {
    // Input validation
    if (!username?.trim()) {
      throw new Error('Username is required');
    }
    if (!password) {
      throw new Error('Password is required');
    }
    if (password.length < 6) {
      throw new Error('Password must be at least 6 characters long');
    }

    // Rate limiting and state checks
    this.rateLimiters.login();
    this._checkOperationState('login');

    this._setOperationState('login', true);
    try {
      const formData = new URLSearchParams();
      formData.append('username', username.trim().toLowerCase());
      formData.append('password', password);

      const response = await formEncodedApiClient.post('/token', formData, {
        timeout: 10000 // 10 second timeout for login
      });

      // Refresh CSRF token after successful login
      try {
        await csrfManager.refresh();
      } catch (csrfError) {
        console.warn('[AuthService] CSRF refresh failed after login:', csrfError.message);
        // Don't fail login for CSRF issues
        csrfManager.reset();
      }

      const data = response.data;

      // When MFA is enabled, /token returns { mfa_required: true } with only
      // a short-lived session_id cookie set (no access/refresh token yet) --
      // the fallback fetch below would 401 against /users/me since there's
      // no access-token cookie, so it must be skipped here rather than
      // treated as "login succeeded with no user data" (the previous
      // behavior: silently landed the user back on the login page with no
      // feedback and no route to the MFA code-entry screen).
      if (data.mfa_required) {
        return { mfaRequired: true };
      }

      // The /token response embeds a deliberately minimal `user` object
      // (id/email/username/is_active only -- see auth.py's `user_data`
      // comment "be careful not to expose sensitive info"), with no
      // `role`/`is_admin`. Since that object is always truthy, the old
      // `!data.user` check never fired, so `state.auth.user.role` stayed
      // undefined for the whole session -- every role-gated check (e.g. the
      // admin route guard) failed until something else (a full page
      // reload's AuthProvider re-fetch) later replaced it with the full
      // profile. Fetch the full profile whenever the embedded user is
      // missing OR missing its role field.
      if ((!data.user || data.user.role === undefined) && response.status === 200) {
        try {
          const userResponse = await apiClient.get('/users/me', { timeout: 5000 });
          data.user = userResponse.data;
        } catch (userError) {
          console.warn('[AuthService] Failed to fetch user data after login:', userError.message);
          // Continue without user data - it will be fetched later
        }
      }

      //console.log('[AuthService] Login successful');
      return { user: data.user, mfaSetupRequired: !!data.mfa_setup_required };

    } catch (error) {
      const normalizedError = normalizeError(error);
      
      // Log failed login attempts for security monitoring
      if (process.env.NODE_ENV === 'production') {
        console.warn('[AuthService] Login failed for username:', username.trim(), 'Error:', normalizedError);
      }
      
      throw new Error(normalizedError);
    } finally {
      this._setOperationState('login', false);
    }
  }

  // Second step of MFA login: the session_id cookie set by the mfa_required
  // /token response (5 min TTL) is the only auth this call has -- there's no
  // access-token cookie yet, so no `current_user` dependency on the backend
  // route (mfa_endpoint.py's /mfa/login-verify) and no Authorization header
  // needed here either.
  async mfaLoginVerify(code) {
    try {
      const response = await apiClient.post('/mfa/login-verify', { code });

      try {
        await csrfManager.refresh();
      } catch (csrfError) {
        csrfManager.reset();
      }

      return { user: response.data.user };
    } catch (error) {
      throw new Error(normalizeError(error, 'Invalid or expired code'));
    }
  }

  async register(userData) {
    // Input validation
    if (!userData?.username?.trim()) {
      throw new Error('Username is required');
    }
    if (!userData?.email?.trim()) {
      throw new Error('Email is required');
    }
    if (!userData?.password) {
      throw new Error('Password is required');
    }
    if (userData.password.length < 8) {
      throw new Error('Password must be at least 8 characters long');
    }
    
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userData.email.trim())) {
      throw new Error('Please enter a valid email address');
    }

    // Rate limiting and state checks
    this.rateLimiters.register();
    this._checkOperationState('register');

    this._setOperationState('register', true);
    try {
      // Ensure CSRF token is available
      if (!csrfManager.getCurrentToken()) {
        await csrfManager.fetchToken();
      }

      const cleanUserData = {
        ...userData,
        username: userData.username.trim().toLowerCase(),
        email: userData.email.trim().toLowerCase()
      };

      const response = await apiClient.post('/users/register', cleanUserData, {
        timeout: 15000 // 15 second timeout for registration
      });

      //console.log('[AuthService] Registration successful');
      return { user: response.data };

    } catch (error) {
      throw new Error(normalizeError(error, 'Registration failed'));
    } finally {
      this._setOperationState('register', false);
    }
  }

  async logout() {
    this._checkOperationState('logout');
    this._setOperationState('logout', true);

    try {
      // Call logout endpoint with timeout
      await apiClient.post('/logout', {}, { timeout: 5000 });
    } catch (error) {
      console.warn('[AuthService] Logout API call failed:', error.message);
      // Continue with cleanup even if API call fails
    } finally {
      // Always perform cleanup
      csrfManager.reset();
      
      // Clear any cached data
      if (tokenBlacklistService?.clear) {
        try {
          tokenBlacklistService.clear();
        } catch (clearError) {
          console.warn('[AuthService] Token blacklist clear failed:', clearError.message);
        }
      }

      // Dispatch logout event
      window.dispatchEvent(new CustomEvent('auth-logout'));
      
      this._setOperationState('logout', false);
      //console.log('[AuthService] Logout completed');
    }
  }

  async refreshToken() {
    // 🛑 Check for refresh cookie before calling backend
    if (!document.cookie.includes("refresh_token")) {
      throw new Error("No refresh token available");
    }
    this.rateLimiters.refresh();
    this._checkOperationState('refresh');
    this._setOperationState('refresh', true);

    try {
      const response = await apiClient.post('/refresh', {}, {
        timeout: 8000 // 8 second timeout for refresh
      });

      // Refresh CSRF token as well
      try {
        await csrfManager.refresh();
      } catch (csrfError) {
        console.warn('[AuthService] CSRF refresh failed during token refresh:', csrfError.message);
      }

      const data = response.data;

      // Fetch full user data if not included, or if only the minimal
      // id/email/username/is_active shape came through (see the matching
      // comment in login() -- role-gated checks need the full profile).
      if (!data.user || data.user.role === undefined) {
        try {
          const userResponse = await apiClient.get('/users/me', { timeout: 5000 });
          data.user = userResponse.data;
        } catch (userError) {
          console.warn('[AuthService] Failed to fetch user data during refresh:', userError.message);
        }
      }

      return data;

    } catch (error) {
      // Reset CSRF on refresh failure
      csrfManager.reset();
      
      const status = error.response?.status;
      if (status === 401) {
        // Emit auth error event for global handling
        window.dispatchEvent(new CustomEvent('auth-error', { detail: error }));
        throw new Error('Session expired. Please login again.');
      }
      
      console.warn('[AuthService] Token refresh failed:', error.message);
      throw new Error(normalizeError(error, 'Token refresh failed'));
    } finally {
      this._setOperationState('refresh', false);
    }
  }

  async getUserProfile() {
    try {
      const response = await apiClient.get('/users/me', {
        timeout: 8000 // 8 second timeout
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Authentication required');
      }
      throw new Error(normalizeError(error, 'Failed to fetch user profile'));
    }
  }

  async updateUserProfile(userData) {
    if (!userData || typeof userData !== 'object') {
      throw new Error('Valid user data is required');
    }

    try {
      const response = await apiClient.put('/users/me/update', userData, {
        timeout: 10000 // 10 second timeout
      });
      return response.data;
    } catch (error) {
      throw new Error(normalizeError(error, 'Failed to update profile'));
    }
  }

  async changePassword(passwordData) {
    if (!passwordData?.currentPassword || !passwordData?.newPassword) {
      throw new Error('Current password and new password are required');
    }
    
    if (passwordData.newPassword.length < 8) {
      throw new Error('New password must be at least 8 characters long');
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      throw new Error('New password must be different from current password');
    }

    try {
      const response = await apiClient.put('/users/me/password', passwordData, {
        timeout: 10000 // 10 second timeout
      });
      return response.data;
    } catch (error) {
      throw new Error(normalizeError(error, 'Failed to change password'));
    }
  }

  // Debug and status methods
  getServiceStatus() {
    return {
      initialized: this.isInitialized,
      operationStates: { ...this._operationStates },
      csrf: csrfManager.getStatus?.() || null,
      blacklist: tokenBlacklistService?.getStatus?.() || null,
    };
  }

  getCsrfToken() {
    return csrfManager.getCurrentToken();
  }

  async refreshCsrfToken() {
    return csrfManager.refresh();
  }

  // Clear all operation states (useful for testing)
  _clearOperationStates() {
    Object.keys(this._operationStates).forEach(key => {
      this._operationStates[key] = { inProgress: false, lastAttempt: 0 };
    });
  }
}

export const authService = new AuthService();
export default authService;